/**
 * Gates the app on authentication + access:
 *   no token            → SignIn screen
 *   token, loading /me  → spinner
 *   member, no events   → "no access yet, ask the admin"
 *   otherwise           → the app
 */
import { useEffect, type ReactNode } from 'react';
import { IconMoodSad } from '@tabler/icons-react';
import { useAuthStore } from '../../state/authStore.ts';
import { getMe } from '../../lib/apiClient.ts';
import { SignIn } from './SignIn.tsx';
import { NoAccess } from './NoAccess.tsx';
import { Spinner } from '../ui/Spinner.tsx';
import { EmptyState } from '../ui/EmptyState.tsx';
import { Button } from '../ui/Button.tsx';

/** Dedupes the /me fetch across StrictMode's double-mount (module scope). */
let meFetchInFlight = false;

export function AuthGate({ children }: { children: ReactNode }) {
  const token = useAuthStore((s) => s.token);
  const me = useAuthStore((s) => s.me);
  const loading = useAuthStore((s) => s.loading);
  const setMe = useAuthStore((s) => s.setMe);
  const setLoading = useAuthStore((s) => s.setLoading);
  const signOut = useAuthStore((s) => s.signOut);

  // Load /me whenever we have a token but no profile yet.
  //
  // NOTE: we deliberately do NOT cancel the in-flight request on cleanup. Under
  // React 18 StrictMode (dev) the effect mounts → cleans up → remounts; a
  // cancel-on-cleanup guard there can drop the only successful response and
  // leave `loading` stuck true forever (the blank-spinner bug). Instead a
  // module-ref dedupes concurrent fetches and we always settle loading.
  useEffect(() => {
    if (!token || me || meFetchInFlight) return;
    meFetchInFlight = true;
    setLoading(true);
    getMe()
      .then((profile) => setMe({ ...profile }))
      .catch(() => {
        // 401 already triggers signOut via the API layer; other errors → clean sign-out.
        signOut();
      })
      .finally(() => {
        meFetchInFlight = false;
        setLoading(false);
      });
  }, [token, me, setMe, setLoading, signOut]);

  if (!token) return <SignIn />;

  if (loading || !me) {
    return (
      <div className="flex min-h-screen-dvh items-center justify-center text-accent">
        <Spinner className="h-7 w-7" />
      </div>
    );
  }

  // Members with no granted events can't see anything yet.
  if (me.role !== 'admin' && (!me.events || me.events.length === 0)) {
    return <NoAccess email={me.email} onSignOut={signOut} />;
  }

  return <>{children}</>;
}

/** Standalone fallback for an unexpected auth error (kept for completeness). */
export function AuthError() {
  const signOut = useAuthStore((s) => s.signOut);
  return (
    <EmptyState
      icon={IconMoodSad}
      title="Sign-in problem"
      description="Something went wrong with your session."
      action={
        <Button variant="primary" onClick={signOut}>
          Try again
        </Button>
      }
    />
  );
}
